import { useEffect, useRef, useState } from 'react';
import { updateProfileName } from '../services/api';

const USER_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ width: '1.75rem', height: '1.75rem' }}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const INFO_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ width: '0.875rem', height: '0.875rem', flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

/**
 * First-run skippable name prompt modal.
 *
 * Props:
 *   show          boolean
 *   defaultName   string  — pre-filled value (from auth provider)
 *   sourceHint    string  — e.g. "From your GitHub account"
 *   onClose       (savedName: string | null) => void
 *                         null = skipped, string = saved name
 */
export default function NamePromptModal({ show, defaultName = '', sourceHint = '', onClose }) {
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setName(defaultName);
      setSaving(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [show, defaultName]);

  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (e.key === 'Escape') handleSkip();
      if (e.key === 'Enter') handleSave();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [show, name]);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      const result = await updateProfileName(name);
      showToast(result.name ? `Saved — "${result.name}"` : 'Name cleared');
      setTimeout(() => onClose(result.name || ''), 800);
    } catch {
      showToast('Could not save — try again');
      setSaving(false);
    }
  }

  function handleSkip() {
    showToast('You can set your name later in Settings');
    setTimeout(() => onClose(null), 800);
  }

  const initial = name.trim().charAt(0).toUpperCase();
  const hasName = Boolean(name.trim());
  const hasHint = Boolean(sourceHint);

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 1050 }}>
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="npTitle"
          style={{ maxWidth: 400 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Body */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-2xl) var(--space-xl) var(--space-lg)',
            textAlign: 'center',
          }}>
            {/* Avatar */}
            <div style={{
              width: '4rem',
              height: '4rem',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--paper-stack)',
              border: '1px solid var(--rule)',
              color: hasName ? 'var(--accent)' : 'var(--rule)',
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--weight-semibold)',
              lineHeight: 1,
              transition: 'all 0.15s ease',
            }}>
              {hasName ? initial : USER_ICON}
            </div>

            <h1 id="npTitle" style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--ink)',
              letterSpacing: '-0.01em',
              margin: 0,
            }}>
              What should we call you?
            </h1>

            <p style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--ink-muted)',
              lineHeight: 'var(--leading)',
              maxWidth: '30ch',
              margin: 0,
            }}>
              You can change this anytime in Settings.
            </p>

            {/* Source hint */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
              padding: 'var(--space-2xs) var(--space-md)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--weight-medium)',
              color: hasHint ? 'var(--info-text)' : 'var(--ink-muted)',
              background: hasHint ? 'var(--info-bg)' : 'var(--paper-stack)',
              border: `1px solid ${hasHint ? 'var(--info-border)' : 'var(--rule)'}`,
              borderRadius: '999px',
            }}>
              {INFO_ICON}
              <span>{hasHint ? sourceHint : 'No name from provider'}</span>
            </div>

            {/* Input */}
            <div style={{
              width: '100%',
              maxWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-xs)',
              textAlign: 'left',
            }}>
              <label
                htmlFor="npNameInput"
                style={{
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--ink-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Name
              </label>
              <input
                ref={inputRef}
                id="npNameInput"
                className="ds-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                maxLength={100}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-sm)',
          }}>
            <button
              onClick={handleSkip}
              disabled={saving}
              style={{
                flexShrink: 0,
                padding: 'var(--space-sm) var(--space-md)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--ink-muted)',
                background: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
              }}
            >
              Skip for now
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'OK'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 'var(--space-xl)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
          padding: 'var(--space-md) var(--space-xl)',
          background: 'var(--paper)',
          color: 'var(--ink)',
          border: 'var(--border-thin)',
          borderLeft: '3px solid var(--success-text)',
          borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-md)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--weight-medium)',
          maxWidth: 'calc(100vw - 2rem)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}
    </>
  );
}
